import { useState } from 'react'
import { NavLink, useNavigate, useParams } from 'react-router-dom'

import {
  Button,
  ChevronDownIcon,
  DropdownMenu,
  FolderIcon,
  Text,
} from '@buffer-mono/popcorn'

import { FeedItemIcon } from '~publish/pages/Create/Feed/components/FeedIcon/FeedIcon'

import type { ChipItem } from '../../types'

import styles from './MoreFeedsDropdown.module.css'

import type { JSX } from 'react'

interface MoreFeedsDropdownProps {
  overflowItems: ChipItem[]
  buttonClassName?: string
}

export function MoreFeedsDropdown({
  overflowItems,
  buttonClassName,
}: MoreFeedsDropdownProps): JSX.Element {
  const { feedId } = useParams<{ feedId?: string }>()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)

  // The trigger is hidden rather than unmounted while nothing overflows, so an
  // open menu would otherwise linger, empty, over a trigger nobody can see.
  const isOpenAndHasItems = isOpen && overflowItems.length > 0

  return (
    <DropdownMenu
      modal={false}
      open={isOpenAndHasItems}
      onOpenChange={setIsOpen}
    >
      <DropdownMenu.Trigger>
        <Button variant="secondary" size="small" className={buttonClassName}>
          More Feeds
          <ChevronDownIcon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start">
        {overflowItems.map((item: ChipItem) => {
          if (item.type === 'feed') {
            return (
              <DropdownMenu.Item key={item.data.id} asChild>
                <NavLink
                  to={`/create/feeds/${item.data.id}`}
                  className={styles.dropdownItem}
                >
                  <FeedItemIcon feedName={item.data.name} url={item.data.url} />
                  <Text truncate>{item.data.name}</Text>
                </NavLink>
              </DropdownMenu.Item>
            )
          }

          // For collections, use a submenu with radio items
          return (
            <DropdownMenu.Sub key={item.data.id}>
              <DropdownMenu.SubTrigger>
                <FolderIcon />
                {item.data.name}
              </DropdownMenu.SubTrigger>
              <DropdownMenu.SubMenu>
                {/* View all in collection option */}
                <DropdownMenu.Item asChild>
                  <NavLink
                    to={`/create/feed/collections/${item.data.id}`}
                    className={styles.dropdownItem}
                  >
                    <FolderIcon />
                    View all in {item.data.name}
                  </NavLink>
                </DropdownMenu.Item>

                {item.data.feeds.length > 0 ? (
                  <>
                    <DropdownMenu.Separator />
                    <DropdownMenu.RadioGroup value={feedId}>
                      {item.data.feeds.map((feed) => (
                        <DropdownMenu.RadioItem
                          key={feed.id}
                          value={feed.id}
                          onSelect={(): void => {
                            navigate(`/create/feeds/${feed.id}`)
                          }}
                        >
                          <FeedItemIcon feedName={feed.name} url={feed.url} />
                          <Text truncate>{feed.name}</Text>
                        </DropdownMenu.RadioItem>
                      ))}
                    </DropdownMenu.RadioGroup>
                  </>
                ) : (
                  <>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item disabled>
                      No feeds in this collection
                    </DropdownMenu.Item>
                  </>
                )}
              </DropdownMenu.SubMenu>
            </DropdownMenu.Sub>
          )
        })}
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
